import type { ReactElement, ReactNode } from 'react';

type ReactComponentType = string | { name: string };

const MAX_SEARCH_TERM_LENGTH = 200;

// Helper to check if an element is null, undefined, or boolean
const isNullish = (element: ReactNode): element is null | undefined | boolean =>
  !element || typeof element === 'boolean';

export type GroupSearchItem = {
  label: string;
  groupKey: string;
  tabName: string;
  sectionId?: string;
  path?: string[];
  subTabName?: string;
};

export type SearchTree = {
  searchTerms: string[];
  children: SearchTree[];
  sectionId?: string;
  heading?: string;
};

export type FlattenedSearchItem = {
  searchTerm: string;
  sectionId?: string;
  groupKey: string;
  path: string[];
};

// Helper to clean and deduplicate terms
const cleanTerms = (texts: string[]) =>
  Array.from(
    new Set(
      texts
        .map((t) => t.trim())
        .filter((t) => t.length > 0 && t.length < MAX_SEARCH_TERM_LENGTH),
    ),
  );

const IGNORED_COMPONENTS = ['Button', 'Link'];

const isIgnoredComponent = (type: ReactComponentType | undefined) =>
  IGNORED_COMPONENTS.includes(
    typeof type === 'string' ? type : (type?.name ?? ''),
  );

/**
 * Checks whether a function component appears to use React hooks by inspecting
 * its source code. Components with hooks cannot be called as plain functions
 * during render (e.g., inside useMemo) because the hooks would register in the
 * parent component's fiber, causing "Rendered fewer hooks than expected" errors.
 */

// biome-ignore lint/complexity/noBannedTypes: Function without known shape expected
function appearsToUseHooks(fn: Function): boolean {
  try {
    const source = fn.toString();
    // Match hook calls: useXxx( or .useXxx( where X is uppercase
    return /\.?use[A-Z]/.test(source);
  } catch {
    return true; // Assume hooks if we can't inspect the source
  }
}

/**
 * Expands the top-level function components in a ReactNode by calling them
 * with their props. This allows the search extraction to see inside components
 * like TimeoutSettings, FailureHandling, etc. that render their own content
 * rather than receiving it as children props.
 *
 * Only expands hook-free function components. Components with hooks are left
 * as-is to avoid corrupting React's internal state.
 */
function tryExpandChildren(element: ReactNode): ReactNode {
  if (
    isNullish(element) ||
    typeof element === 'string' ||
    typeof element === 'number'
  ) {
    return element;
  }
  if (Array.isArray(element)) {
    return element.map(tryExpandChildren);
  }
  if (typeof element !== 'object' || !('props' in element)) {
    return element;
  }

  const { type, props } = element as ReactElement;
  if (typeof type === 'function' && !appearsToUseHooks(type)) {
    try {
      // biome-ignore lint/complexity/noBannedTypes: Function without known shape expected
      return (type as Function)(props);
    } catch {
      return element;
    }
  }
  return element;
}

function getComponentTypeName(type: ReactElement['type']): string {
  if (typeof type === 'string') {
    return type;
  }
  if (typeof type === 'function') {
    return (type as any).displayName || type.name || '';
  }
  if (typeof type === 'object' && type && 'name' in type) {
    return (type as { name: string }).name;
  }
  return '';
}

export function flattenSearchTree(
  tree: SearchTree,
  groupKey: string,
  parentSectionId?: string,
  parentPath: string[] = [],
): FlattenedSearchItem[] {
  const currentSectionId = tree.sectionId ?? parentSectionId;
  const currentPath = tree.heading ? [...parentPath, tree.heading] : parentPath;

  const result: FlattenedSearchItem[] = tree.searchTerms
    .filter((term) => !currentPath.includes(term)) // Don't include the heading itself as a search term
    .map((term) => ({
      searchTerm: term,
      sectionId: currentSectionId,
      groupKey,
      path: currentPath,
    }));

  // Include heading as a searchable term with a parent path to avoid redundant breadcrumbs
  if (tree.heading) {
    result.unshift({
      searchTerm: tree.heading,
      sectionId: currentSectionId,
      groupKey,
      path: parentPath,
    });
  }

  for (const child of tree.children) {
    result.push(
      ...flattenSearchTree(child, groupKey, currentSectionId, currentPath),
    );
  }
  return result;
}

function extractTextFromProps(props: Record<string, unknown>): string[] {
  const texts: string[] = [];
  const propsToExtract = [
    'title',
    'label',
    'placeholder',
    'description',
    'content',
    'aria-label',
  ];

  for (const p of propsToExtract) {
    if (typeof props[p] === 'string') {
      if (p === 'description' && props.label !== undefined) {
        continue;
      }
      texts.push(props[p] as string);
    }
  }
  return texts;
}

function extractTextFromChildren(
  props: { children?: ReactNode; className?: string },
  type: ReactComponentType | undefined,
  shallow: boolean,
): string[] {
  if (!props.children) {
    return [];
  }

  const isLabel =
    type === 'label' || (typeof type === 'object' && type?.name === 'label');
  const hasHeadingClass = props.className?.includes('font-heading');

  if (typeof props.children === 'string' && (isLabel || hasHeadingClass)) {
    return [props.children];
  }

  return extractRawText(
    props.children,
    shallow,
    !!(isLabel || hasHeadingClass),
  );
}

function extractHeadingFromElement(
  element: ReactNode,
  depth = 0,
  maxDepth = 3,
): string | undefined {
  if (isNullish(element) || depth > maxDepth) {
    return undefined;
  }

  if (Array.isArray(element)) {
    for (const child of element) {
      const heading = extractHeadingFromElement(child, depth, maxDepth);
      if (heading) {
        return heading;
      }
    }
    return undefined;
  }

  if (typeof element !== 'object' || !('props' in element)) {
    return undefined;
  }

  const reactElement = element as ReactElement;
  const { props, type } = reactElement;
  const typeName = getComponentTypeName(type);

  // Skip SearchableSection components when looking for headings - we don't want nested section headings
  if (typeName === 'SearchableSection') {
    return undefined;
  }

  // Check if this element has font-heading class
  if (
    props.className?.includes('font-heading') &&
    typeof props.children === 'string'
  ) {
    return props.children;
  }

  // Recursively search for headings in children
  if (props.children) {
    return extractHeadingFromElement(props.children, depth + 1, maxDepth);
  }

  return undefined;
}

function extractRawText(
  element: ReactNode,
  shallow = false,
  forceExtract = false,
): string[] {
  if (isNullish(element)) {
    return [];
  }

  if (typeof element === 'string') {
    return forceExtract ? [element] : [];
  }

  if (typeof element === 'number') {
    return forceExtract ? [String(element)] : [];
  }

  if (Array.isArray(element)) {
    return element.flatMap((e) => extractRawText(e, shallow, forceExtract));
  }

  if (typeof element !== 'object' || !('props' in element)) {
    return [];
  }

  const reactElement = element as ReactElement;
  const { props, type } = reactElement;
  const typeName = getComponentTypeName(type);

  if (typeName === 'SearchableSection') {
    // When shallow, don't recurse into SearchableSection children at all
    // When not shallow, explicitly process all nested content
    return shallow ? [] : extractRawText(props.children, false, forceExtract);
  }

  if (isIgnoredComponent(type as ReactComponentType)) {
    return [];
  }

  return [
    ...extractTextFromProps(props),
    ...extractTextFromChildren(props, type as ReactComponentType, shallow),
  ];
}

function buildSearchTree(element: ReactNode): SearchTree[] {
  if (isNullish(element)) {
    return [];
  }
  if (Array.isArray(element)) {
    return element.flatMap(buildSearchTree);
  }

  if (typeof element !== 'object' || !('props' in element)) {
    return [];
  }

  const reactElement = element as ReactElement;
  const { props, type } = reactElement;
  const typeName = getComponentTypeName(type);

  if (typeName === 'SearchableSection') {
    // Expand direct function component children (e.g., TimeoutSettings) so
    // that the walkers can see their internal JSX structure.
    const expandedChildren = tryExpandChildren(props.children);
    const heading = extractHeadingFromElement(expandedChildren);
    const children = buildSearchTree(expandedChildren);
    const extractedTerms = extractRawText(expandedChildren, true, false);
    const explicitTerms: string[] = Array.isArray(props.searchTerms)
      ? props.searchTerms
      : [];
    return [
      {
        searchTerms: cleanTerms([...extractedTerms, ...explicitTerms]),
        children,
        sectionId: props.id,
        heading: heading ?? explicitTerms[0],
      },
    ];
  }

  if (isIgnoredComponent(type as ReactComponentType)) {
    return [];
  }
  return props?.children ? buildSearchTree(props.children) : [];
}

export function extractGroupSearchItems(
  groupKey: string,
  tabName: string,
  groupJsx: ReactNode,
): GroupSearchItem[] {
  if (isNullish(groupJsx)) {
    return [];
  }

  const children = buildSearchTree(groupJsx);

  const tree: SearchTree = {
    searchTerms: cleanTerms(extractRawText(groupJsx, true, false)),
    children,
    heading: extractHeadingFromElement(groupJsx), // Extract top-level heading
  };

  return flattenSearchTree(tree, groupKey).map((item) => ({
    label: item.searchTerm,
    groupKey: item.groupKey,
    tabName,
    sectionId: item.sectionId,
    path: item.path.length > 0 ? item.path : undefined,
  }));
}
